import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from './utils/Navbr';
import toast from './utils/Toast';
import Loader from './utils/Loader';
import './style/PerformanceReport.css';

const PerformanceReport = () => {
    const { id, dateRange } = useParams();

    // --- State ---
    const [loading, setLoading] = useState(false);
    const [loader, setLoader] = useState(true);
    const navigate = useNavigate();
    // Filters
    const [filters, setFilters] = useState({
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        selectedOffers: [],
        selectedPubs: [],
        search: '',
        type: 'both' // 'clicks', 'conversions', 'both'
    });
    const [offersList, setOffersList] = useState([]);
    const [publishersList, setPublishersList] = useState([]);

    const [showOfferDropdown, setShowOfferDropdown] = useState(false);
    const [showPubDropdown, setShowPubDropdown] = useState(false);

    const [tableData, setTableData] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, totalPages: 1, totalRows: 0 });

    const [activeModal, setActiveModal] = useState(null);
    const [modalData, setModalData] = useState(null);

    // --- Initialization ---
    useEffect(() => {
        const adminToken = getCookie('adminToken');

        const init = async () => {
            try {
                // ✅ 1. Admin Auth Check
                const authRes = await axios.post('https://publisher.cashbolt.in.net/server/', {
                    action: 'checkAuthAdmin',
                    adminToken
                });

                if (!authRes.data.status) {
                    toast.error(authRes.data.message || 'Admin session expired');
                    navigate('/cbMediaAdmin7/login');
                    return;
                }

                fetchData(1);

            } catch (err) {
                toast.error('Admin auth check failed');
                navigate('/cbMediaAdmin7/login');
            } finally {
                setLoader(false);
            }
        };
        if (dateRange) {
            const [start, end] = dateRange.split('_');
            setFilters(prev => ({ ...prev, startDate: start, endDate: end }));
        }

        if (id) {
            if (id.startsWith('OFF')) {
                setFilters(prev => ({ ...prev, selectedOffers: [id] }));
            } else if (id.startsWith('PUB')) {
                setFilters(prev => ({ ...prev, selectedPubs: [id] }));
            }
        }

        init();
        // eslint-disable-next-line
    }, [id, dateRange]);

    // --- Fetch Data ---
    const getCookie = (name) => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    };

    const fetchData = async (page = 1) => {
        setLoading(true);
        const adminToken = getCookie('adminToken');

        try {
            const response = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'fetchPerformanceReport',
                adminToken: adminToken || 'demo_token',
                filters: { ...filters, page }
            });

            if (response.data.status === true) {
                setTableData(response.data.data);
                setPagination(response.data.pagination);

                // ✅ NEW: dropdown lists from backend
                if (response.data.offers) setOffersList(response.data.offers);
                if (response.data.publishers) setPublishersList(response.data.publishers);

            }
        } catch (error) {
            console.error(error);
        } finally {
            setTimeout(() => setLoading(false), 600);
        }
    };


    // --- Handlers ---
    const handleDateChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const toggleSelection = (listType, itemId) => {
        setFilters(prev => {
            const list = listType === 'offer' ? prev.selectedOffers : prev.selectedPubs;
            const newList = list.includes(itemId)
                ? list.filter(id => id !== itemId)
                : [...list, itemId];

            return {
                ...prev,
                [listType === 'offer' ? 'selectedOffers' : 'selectedPubs']: newList
            };
        });
    };

    const selectAll = (listType) => {
        if (listType === 'offer') {
            const allIds = offersList.map(o => o.id);
            setFilters(prev => ({ ...prev, selectedOffers: prev.selectedOffers.length === allIds.length ? [] : allIds }));
        } else {
            const allIds = publishersList.map(p => p.id);
            setFilters(prev => ({ ...prev, selectedPubs: prev.selectedPubs.length === allIds.length ? [] : allIds }));
        }
    };

    const handleTypeChange = (newType) => {
        setFilters(prev => ({ ...prev, type: newType }));
        // Note: fetchData is called via the Apply button, or you can call it here if you want instant update
    };

    const openParamsModal = (data) => {
        setModalData(data);
        setActiveModal('params');
    };
    const openDetailsModal = (data) => {
        setModalData(data);
        setActiveModal('details');
    };
    const closeModal = () => {
        setActiveModal(null);
        setModalData(null);
    };

    const handleExport = (type) => {
        toast.info(`Exporting ${type.toUpperCase()}...`, 3000);
    };
    if (loader) return <Loader />;
    return (
        <div className="perf-wrapper_perf">
            <Navbar role="Administrator" isAdmin={true} />

            <div className="bg-shape_perf shape-1_perf"></div>
            <div className="bg-shape_perf shape-2_perf"></div>

            <div className="perf-container_perf">

                <div className="perf-header_perf">
                    <h2>Performance <span className="highlight_perf">Report</span></h2>
                    <p>Granular data analysis for clicks and conversions</p>
                </div>

                {/* --- FILTERS SECTION --- */}
                <div className="filters-card_perf">

                    {/* Row 1: Dates & Search */}
                    <div className="filter-row_perf">
                        <div className="date-group_perf">
                            <div className="custom-input-box_perf">
                                <label>Start Date</label>
                                <input type="date" name="startDate" value={filters.startDate} onChange={handleDateChange} />
                            </div>
                            <span className="separator_perf"></span>
                            <div className="custom-input-box_perf">
                                <label>End Date</label>
                                <input type="date" name="endDate" value={filters.endDate} onChange={handleDateChange} />
                            </div>
                        </div>

                        <div className="search-box_perf">
                            <input
                                type="text"
                                placeholder="Search Click ID, Offer Name, Pub Name..."
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            />
                            <button className="search-btn_perf">🔍</button>
                        </div>
                    </div>

                    {/* Row 2: Custom Multi-Selects & Type Toggle */}
                    <div className="filter-row_perf">

                        {/* Offer Multi-Select */}
                        <div className="custom-select-wrapper_perf">
                            <div className="select-trigger_perf" onClick={() => setShowOfferDropdown(!showOfferDropdown)}>
                                {filters.selectedOffers.length > 0
                                    ? <div className="tags-preview_perf">
                                        {filters.selectedOffers.slice(0, 2).map(id => (
                                            <span key={id} className="tag_perf">{offersList.find(o => o.id === id)?.name || id}</span>
                                        ))}
                                        {filters.selectedOffers.length > 2 && <span className="tag-more_perf">+{filters.selectedOffers.length - 2}</span>}
                                    </div>
                                    : <span>Select Offers</span>
                                }
                                <span className="arrow_perf">▼</span>
                            </div>
                            {showOfferDropdown && (
                                <div className="dropdown-menu_perf">
                                    <div className="dd-item_perf select-all_perf" onClick={() => selectAll('offer')}>
                                        Select All
                                    </div>
                                    {offersList.map(offer => (
                                        <div
                                            key={offer.id}
                                            className={`dd-item_perf ${filters.selectedOffers.includes(offer.id) ? 'selected' : ''}`}
                                            onClick={() => toggleSelection('offer', offer.id)}
                                        >
                                            <span className="checkbox_perf"></span>
                                            {offer.name} <small>({offer.id})</small>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Publisher Multi-Select */}
                        <div className="custom-select-wrapper_perf">
                            <div className="select-trigger_perf" onClick={() => setShowPubDropdown(!showPubDropdown)}>
                                {filters.selectedPubs.length > 0
                                    ? <div className="tags-preview_perf">
                                        {filters.selectedPubs.slice(0, 2).map(id => (
                                            <span key={id} className="tag_perf pub">{publishersList.find(p => p.id === id)?.name || id}</span>
                                        ))}
                                        {filters.selectedPubs.length > 2 && <span className="tag-more_perf">+{filters.selectedPubs.length - 2}</span>}
                                    </div>
                                    : <span>Select Publishers</span>
                                }
                                <span className="arrow_perf">▼</span>
                            </div>
                            {showPubDropdown && (
                                <div className="dropdown-menu_perf">
                                    <div className="dd-item_perf select-all_perf" onClick={() => selectAll('pub')}>
                                        Select All
                                    </div>
                                    {publishersList.map(pub => (
                                        <div
                                            key={pub.id}
                                            className={`dd-item_perf ${filters.selectedPubs.includes(pub.id) ? 'selected' : ''}`}
                                            onClick={() => toggleSelection('pub', pub.id)}
                                        >
                                            <span className="checkbox_perf"></span>
                                            {pub.name} <small>({pub.id})</small>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Type Toggle */}
                        <div className="type-toggle_perf">
                            <button
                                className={filters.type === 'clicks' ? 'active' : ''}
                                onClick={() => handleTypeChange('clicks')}
                            >Clicks</button>
                            <button
                                className={filters.type === 'conversions' ? 'active' : ''}
                                onClick={() => handleTypeChange('conversions')}
                            >Conversions</button>
                            <button
                                className={filters.type === 'both' ? 'active' : ''}
                                onClick={() => handleTypeChange('both')}
                            >Both</button>
                        </div>

                        {/* Apply Button */}
                        <button className="apply-btn_perf" onClick={() => fetchData(1)}>
                            APPLY FILTERS
                            <div className="wave_perf"></div>
                        </button>

                    </div>
                </div>

                {/* --- EXPORT BAR --- */}
                <div className="export-bar_perf">
                    <span className="export-label_perf">Export Data As:</span>
                    <button className="exp-btn_perf excel" onClick={() => handleExport('excel')}>EXCEL</button>
                    <button className="exp-btn_perf csv" onClick={() => handleExport('csv')}>CSV</button>
                    <button className="exp-btn_perf json" onClick={() => handleExport('json')}>JSON</button>
                    <button className="exp-btn_perf pdf" onClick={() => handleExport('pdf')}>PDF</button>
                </div>

                {/* --- DATA TABLE --- */}
                <div className="table-wrapper_perf">
                    {loading ? (
                        <div className="loader-container_perf"><div className="spinner_perf"></div><p>Loading Data...</p></div>
                    ) : (
                        <>
                            <div className="table-scroll_perf">
                                <table className="perf-table_perf">
                                    <thead>
                                        <tr>
                                            <th>Click ID</th>
                                            <th>Aff Click ID</th>
                                            <th>Sub Aff ID</th>
                                            <th>Click Time</th>
                                            <th>Event</th>
                                            <th>Payout</th>
                                            <th>Revenue</th>
                                            <th>Is Lead</th>
                                            <th>Valid</th>
                                            <th>Conv. Time</th>
                                            <th>Time Gap</th>
                                            <th className="sticky-right_perf">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tableData.length > 0 ? (
                                            tableData.map((row, idx) => (
                                                <tr key={idx} className="table-row_perf">
                                                    <td className="mono_perf">{row.click_id}</td>
                                                    <td className="mono_perf muted">{row.aff_click_id}</td>
                                                    <td className="mono_perf muted">{row.sub_aff_id}</td>
                                                    <td className="small-text_perf">{row.click_time}</td>
                                                    <td>
                                                        <span className={`badge_perf ${row.is_lead ? 'purchase' : 'install'}`}>
                                                            {row.event}
                                                        </span>
                                                    </td>
                                                    <td className="money_perf">₹{row.payout}</td>
                                                    <td className="money_perf green">₹{row.revenue}</td>
                                                    <td><span className={`status-dot_perf ${row.is_lead ? 'yes' : 'no'}`}></span></td>
                                                    <td><span className={`status-dot_perf ${row.valid ? 'yes' : 'no'}`}></span></td>
                                                    <td className="small-text_perf">{row.conv_time}</td>
                                                    <td className="small-text_perf">{row.time_gap}</td>

                                                    <td className="sticky-right_perf actions-cell_perf">
                                                        <button className="icon-action-btn_perf" title="Custom Params" onClick={() => openParamsModal(row.aff_sub)}>
                                                            ⚙️
                                                        </button>
                                                        <button className="icon-action-btn_perf blue" title="Tech Details" onClick={() => openDetailsModal(row.details)}>
                                                            ℹ️
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="12" style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>No records found matching filters.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <div className="pagination_perf">
                                <button
                                    disabled={pagination.page === 1}
                                    onClick={() => fetchData(pagination.page - 1)}
                                >Prev</button>
                                <span>Page {pagination.page} of {pagination.totalPages}</span>
                                <button
                                    disabled={pagination.page === pagination.totalPages}
                                    onClick={() => fetchData(pagination.page + 1)}
                                >Next</button>
                            </div>
                        </>
                    )}
                </div>

            </div>

            {/* --- MODALS --- */}
            {activeModal && (
                <div className="modal-overlay_perf" onClick={closeModal}>
                    <div className="modal-content_perf" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header_perf">
                            <h3>{activeModal === 'params' ? 'Custom Parameters' : 'Technical Details'}</h3>
                            <button className="close-btn_perf" onClick={closeModal}>&times;</button>
                        </div>
                        <div className="modal-body_perf">
                            {activeModal === 'params' && (
                                <div className="params-grid_perf">
                                    {Object.entries(modalData).map(([key, val]) => (
                                        <div key={key} className="param-item_perf">
                                            <label>{key}</label>
                                            <input type="text" readOnly value={val || '-'} />
                                        </div>
                                    ))}
                                </div>
                            )}
                            {activeModal === 'details' && (
                                <div className="details-list_perf">
                                    <div className="detail-row_perf"><span>IP Address:</span> <strong>{modalData.ip}</strong></div>
                                    <div className="detail-row_perf"><span>Location:</span> <strong>{modalData.location}</strong></div>
                                    <div className="detail-row_perf"><span>Device:</span> <strong>{modalData.device}</strong></div>
                                    <div className="detail-row_perf"><span>Browser:</span> <strong>{modalData.browser}</strong></div>
                                    <div className="detail-row_perf"><span>Fingerprint:</span> <code className="fp_code">{modalData.fingerprint}</code></div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default PerformanceReport;